import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  runInInjectionContext,
  signal
} from '@angular/core';
import {ConfigurationStore} from '../../store/configuration.store';
import {MatDialog} from '@angular/material/dialog';
import {LoginComponent} from '../../login/login.component';
import {AuthenticationService} from '../../services/authentication.service';
import {Router, RouterLink} from '@angular/router';
import {
  BundleCartItem,
  CartItem,
  CartItemType,
  CartService,
  ProductCartItem,
  PromotionCartItem
} from '../../services/cart.service';
import {SharedDialogComponent} from '../../shared-dialog/shared-dialog.component';
import {OrderContextStore} from '../../store/order-context.store';
import {PromosService} from '../../services/promos.service';
import {PromoCodeDialogComponent} from './promo-code-dialog/promo-code-dialog.component';
import {THEME_CONFIG} from '../../../environments/tokens/theme.token';
import {MatIcon} from '@angular/material/icon';
import {FormsModule} from '@angular/forms';
import {MatButton} from '@angular/material/button';
import {DecimalPipe, isPlatformBrowser, NgClass, NgOptimizedImage} from '@angular/common';
import {TenantService} from '../../services/tenant.service';
import {ConfigurationService} from '../../services/configuration.service';
import {NotificationService} from '../../notification/notification.service';
import {NotificationType} from '../../enum/notification-type.enum';
import {TypeOrderEnum} from '../../enum/type-order.enum';
import {toSignal} from '@angular/core/rxjs-interop';
import {catchError, of} from 'rxjs';
import {CartConfigStore} from '../../store/cart-config.store';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  standalone: true,
  imports: [MatIcon, RouterLink, FormsModule, MatButton, NgClass, DecimalPipe, NgOptimizedImage]

})
export class NavbarComponent implements OnInit, OnDestroy {
  promoCodeInput: string = '';
  private platformId = inject(PLATFORM_ID);
  isBrowser = isPlatformBrowser(this.platformId);
  isMenuOpen = false;
  isMenuClosing = false;
  private readonly configStore = inject(ConfigurationStore);
  private authenticationService = inject(AuthenticationService);
  private configService = inject(ConfigurationService);
  private cartConfigStore = inject(CartConfigStore);
  cartConfig = this.cartConfigStore.cartConfig;
  isCartConfigLoaded = this.cartConfigStore.isLoaded;
  private router = inject(Router);
  readonly phones = this.configStore.phones;
  readonly loading = this.configStore.loading;
  readonly logo = this.configStore.logo;
  readonly restoName = this.configStore.name;
  readonly cartService = inject(CartService);
  readonly promosService = inject(PromosService);
  protected readonly themeConfig = inject(THEME_CONFIG)
  CartItemType = CartItemType;
  readonly orderContextStore = inject(OrderContextStore);
  dataOrders: any[] = [];
  private tenantService = inject(TenantService);
  isMilano = false;
  serverCartSynced = signal<boolean>(false);
  private injector = inject(Injector);

  private notificationService = inject(NotificationService);

  @HostListener('window:resize')
  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.isBrowser) return;
    if (window.innerWidth >= 1024 && this.isCartSlideOpen) {
      this.isCartSlideOpen = false;
      document.body.style.overflow = '';
    }
  }

  constructor(private dialog: MatDialog) {
  }

  ngOnInit() {
    this.isMilano = this.tenantService.getActiveTenant() === 'milano';
    this.cartConfigStore.loadConfig();
  }


  toggleMenu() {
    if (this.isMenuOpen) {
      this.closeMenu();
    } else {
      this.isMenuOpen = true;
      this.isMenuClosing = false;
    }
  }


  private parseLocalDate(dateStr?: string): Date | null {
    if (!dateStr) return null;
    const cleanStr = dateStr.split('.')[0].replace('Z', '');
    const parts = cleanStr.split('T');
    if (parts.length !== 2) return new Date(dateStr);

    const [year, month, day] = parts[0].split('-').map(Number);
    const [hours, minutes, seconds] = parts[1].split(':').map(Number);

    return new Date(year, month - 1, day, hours || 0, minutes || 0, seconds || 0);
  }

  private formatTimeDirect(dateStr?: string): string {
    if (!dateStr) return '';
    const timePart = dateStr.split('T')[1];
    return timePart ? timePart.substring(0, 5) : '';
  }

  private formatDateDirect(dateStr?: string): string {
    if (!dateStr) return '';
    const datePart = dateStr.split('T')[0];
    if (!datePart) return '';
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
  }

  isCartDisabled = computed(() => {
    const cart = this.cartConfig();
    if (!cart) return false;
    if (cart.active) return false;

    const now = new Date().getTime();
    const start = this.parseLocalDate(cart.startDate)?.getTime() ?? null;
    const end = this.parseLocalDate(cart.endDate)?.getTime() ?? null;

    if (start && end) return now >= start && now <= end;
    if (start) return now >= start;
    if (end) return now <= end;

    return true;
  });
  getDisabledPeriodMessage = computed<string>(() => {
    const cart = this.cartConfig();
    const contactSuffix = " Merci de contacter l'administration.";

    if (!cart) return 'Les commandes en ligne sont actuellement désactivées.';

    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    const startRaw = cart.startDate?.split('T')[0];
    const endRaw = cart.endDate?.split('T')[0];

    const startTimeStr = this.formatTimeDirect(cart.startDate);
    const endTimeStr = this.formatTimeDirect(cart.endDate);

    const startDateFormatted = this.formatDateDirect(cart.startDate);
    const endDateFormatted = this.formatDateDirect(cart.endDate);

    if (startRaw && endRaw) {
      const startIsToday = startRaw === todayStr;
      const endIsToday = endRaw === todayStr;

      if (startIsToday && endIsToday) {
        return `🛒 Les commandes en ligne sont désactivées de ${startTimeStr} à ${endTimeStr}.` + contactSuffix;
      }

      if (startIsToday) {
        return `🛒 Les commandes en ligne sont désactivées à partir de ${startTimeStr} jusqu'au ${endDateFormatted} à ${endTimeStr}.` + contactSuffix;
      }

      return `🛒 Les commandes en ligne sont désactivées du ${startDateFormatted} à ${startTimeStr} au ${endDateFormatted} à ${endTimeStr}.` + contactSuffix;
    }

    if (startRaw) {
      if (startRaw === todayStr) {
        return `🛒 Les commandes en ligne sont désactivées à partir de ${startTimeStr}.` + contactSuffix;
      }
      return `🛒 Les commandes en ligne sont désactivées à partir du ${startDateFormatted} à ${startTimeStr}.` + contactSuffix;
    }

    if (endRaw) {
      if (endRaw === todayStr) {
        return `🛒 Les commandes en ligne sont désactivées jusqu'à ${endTimeStr}.` + contactSuffix;
      }
      return `🛒 Les commandes en ligne sont désactivées jusqu'au ${endDateFormatted} à ${endTimeStr}.` + contactSuffix;
    }

    return '🛒 Les commandes en ligne sont temporairement désactivées.';
  });

  closeMenu() {
    if (this.isMenuOpen && !this.isMenuClosing) {
      this.isMenuClosing = true;
      setTimeout(() => {
        this.isMenuOpen = false;
        this.isMenuClosing = false;
      }, 300);
    }
  }

  scrollToContact() {
    const element = document.querySelector('#contact-footer');
    if (element) {
      element.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
    if (this.isMenuOpen) {
      this.closeMenu();
    }
  }


  searchPromoCode(): void {
    if (!this.promoCodeInput.trim()) return;

    this.promosService.getPromotionWithCode(this.promoCodeInput.trim()).subscribe({
      next: (response: any) => {
        const promo = response?.body?.data;
        if (!promo) {
          // Code introuvable → ouvrir le dialog pour afficher l'erreur
          this.openPromoCodeDialogWithCode();
          return;
        }

        if (promo.type === 'order-discount') {
          const rules: any[] = promo.payload?.rules || [];
          const selections: any[] = promo.payload?.selections || [];

          const matchingCartItems = this.cartService.items()
            .filter(item => item.type === CartItemType.PRODUCT)
            .filter(item => {
              const productItem = item as ProductCartItem;
              return selections.some((sel: any) => {
                const productMatch = (sel.products || []).some(
                  (p: any) => p.id === productItem.product.id
                );
                if (!productMatch) return false;
                const selSizes: any[] = sel.sizes || [];
                if (selSizes.length === 0) return true;
                const cartSizeObj = productItem.product.pricePerSizes?.find(
                  (pps: any) => pps.size.id === productItem.selectedSize
                );
                const cartSizeName = cartSizeObj?.size?.name || '';
                return selSizes.some((s: any) =>
                  s.id === productItem.selectedSize || s.name === cartSizeName
                );
              });
            }) as ProductCartItem[];

          const promoEligibleTotal = matchingCartItems.reduce(
            (sum, item) => sum + item.total, 0
          );

          const sortedRules = [...rules].sort((a, b) => a.min - b.min);
          const eligibleRule = sortedRules.find(
            (rule: any) =>
              promoEligibleTotal >= rule.min && promoEligibleTotal <= rule.max
          );

          if (eligibleRule) {
            // ✅ Appliquer directement, aucun dialog
            this.cartService.setOrderDiscount({
              promotion: {
                ...promo,
                addedByPromoCode: true,
                promoCode: this.promoCodeInput.trim()
              },
              rule: eligibleRule
            });
            this.promoCodeInput = '';
            return;
          }
        }

        // Autres types ou pas de règle éligible → ouvrir le dialog normal
        this.openPromoCodeDialogWithCode();
      },
      error: () => {
        // Erreur API → ouvrir le dialog pour afficher l'erreur
        this.openPromoCodeDialogWithCode();
      }
    });
  }

  hasOrderDiscountPromoCode(): boolean {
    const discount = this.cartService.orderDiscount();
    return !!discount &&
      discount.promotion?.type === 'order-discount' &&
      discount.promotion?.addedByPromoCode === true;
  }

  private openPromoCodeDialogWithCode(): void {
    const dialogRef = this.dialog.open(PromoCodeDialogComponent, {
      disableClose: true,
      panelClass: 'custom-dialog',
      width: '420px',
      data: {promoCode: this.promoCodeInput.trim()}
    });

    dialogRef.afterClosed().subscribe(() => {
      this.promoCodeInput = '';
    });
  }

  getOrderDiscount(): any {
    return this.cartService.orderDiscount();
  }

  getSubTotal(): number {
    // Le total du panier SANS la réduction
    return this.cartService.totalPrice();
  }

  hasAnyPromoCodeApplied(): boolean {
    // order-discount
    if (this.cartService.orderDiscount()?.promotion?.addedByPromoCode === true) return true;

    // product-offer ou order-offer ajouté par code promo
    const hasPromoItem = this.cartService.promotionItems().some(
      item => item.promotion?.addedByPromoCode === true
    );
    if (hasPromoItem) return true;

    // product-discount ajouté par code promo
    const hasDiscountedProduct = this.cartService.productItems().some(
      item => (item as any).addedByPromoCode === true
    );
    return hasDiscountedProduct;
  }

  getDiscountAmount(): number {
    const discount = this.cartService.orderDiscount();
    if (!discount) return 0;
    const reduction = discount.rule?.reduction;
    if (!reduction) return 0;

    const subTotal = this.getSubTotal();

    if (reduction.type === 'PERCENTAGE') {
      return this.truncate2(subTotal * reduction.value);
    }
    if (reduction.type === 'FIXED_AMOUNT') {
      return this.truncate2(Math.min(reduction.value, subTotal));
    }
    return 0;
  }

  getFinalTotal(): number {
    return this.truncate2(Math.max(0, this.getSubTotal() - this.getDiscountAmount()));
  }

  removeOrderDiscount(): void {
    this.cartService.setOrderDiscount(null);
  }

  readonly orderDiscountBadge = computed(() => {
    const d = this.cartService.orderDiscount();
    if (!d) return '';
    const r = d.rule.reduction;
    if (!r) return '';
    if (r.type === 'FIXED_AMOUNT') return `-${r.value}€`;
    if (r.type === 'PERCENTAGE') return `-${Math.round((r.value || 0) * 100)}%`;
    return '';
  });

  // openDialog(categoryId?: string) {
  //   const isOnProductPage = this.router.url.includes('/product');
  //   this.dialog.open(OrderComponent, {
  //     panelClass: 'custom-dialog',
  //     autoFocus: false,
  //     disableClose: true,
  //     data: {selectedCategoryId: categoryId, fromProductPage: isOnProductPage}
  //   });
  // }
  openDialog(categoryId?: string) {
    this.router.navigate(['/product']);
  }

  openDialogLogin() {
    if (this.authenticationService.isLogin()) {
      this.router.navigate(['/profil']);
      if (this.isMenuOpen) this.closeMenu();
    } else {
      const dialogRef = this.dialog.open(LoginComponent, {
        width: '700px',
        panelClass: 'custom-dialog'
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result === true) this.router.navigate(['/profil']);
      });
    }
  }

  isCartSlideOpen = false;

  toggleCart() {
    this.isCartSlideOpen = !this.isCartSlideOpen;
    if (this.isBrowser) {
      document.body.style.overflow = this.isCartSlideOpen ? 'hidden' : '';
    }

    if (this.isCartSlideOpen) {
      this.checkCartSync();
    }
  }

  private checkCartSync(): void {
    if (!this.authenticationService.isLogin() || this.cartService.isEmpty()) {
      this.serverCartSynced.set(false);
      return;
    }

    this.cartService.getCart().subscribe({
      next: (res) => {
        const payload = res?.body?.data?.payload;
        this.serverCartSynced.set(
          !!payload && this.cartService.isCartSyncedWithServer(payload)
        );
      },
      error: (err) => {
        // Pas de panier côté serveur → cas normal : on garde le panier local, sans erreur
        if (this.isCartNotFoundError(err)) {
          this.serverCartSynced.set(false);
          return;
        }
        // Vraie erreur (500, réseau, ...) → on l'affiche
        this.serverCartSynced.set(false);
        this.notificationService.errorHandler(err);
      }
    });
  }

  private isCartNotFoundError(err: any): boolean {
    const body = err?.error;
    return err?.status === 404 && (
      body?.code === 'not_found_entity' ||
      String(body?.details?.message || '').startsWith('ShoppingCart/')
    );
  }
  getOrderTypeLabelFromKey(key: string): string {
    const typeKey = key as keyof typeof TypeOrderEnum;
    return TypeOrderEnum[typeKey] || key;
  }

  private handleCartSaveError(err: any): void {
    let errorMessage: string = err.error?.details?.message || err.error?.message || '';
    if (errorMessage.includes('QUANTITY_MISS_MATCH')) {
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        "L'administrateur a modifié une quantité d'un produit. Veuillez supprimer la promotion et l'ajouter à nouveau pour être à jour.",
        ''
      );
      return;
    }
    Object.keys(TypeOrderEnum).forEach((key) => {
      if (errorMessage.includes(key)) {
        const translatedLabel = this.getOrderTypeLabelFromKey(key);
        errorMessage = errorMessage.replace(new RegExp(key, 'g'), translatedLabel);
      }
    });

    if (errorMessage.includes('CUSTOMER_NOT_ALLOWED')) {
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Ce code promos appartient a un autre utilisateur vous n’avez pas le droit de l’utiliser`,
        ''
      );
      return;
    }

    if (errorMessage.includes('NOT_STARTED_YET')) {
      const promoCode = errorMessage.split('/')[1] || '';
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Le code promo "${promoCode}" n'est pas encore disponible.`,
        ''
      );
      return;
    }

    if (errorMessage.includes('EXPIRED')) {
      const promoCode = errorMessage.split('/')[1] || '';
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Le code promo "${promoCode}" a expiré.`,
        ''
      );
      return;
    }

    if (errorMessage === 'ORDER_RATE_LIMIT_EXCEEDED') {
      const details = err.error?.details?.details;
      let timeMessage = 'quelques minutes';
      if (details?.allowedAfter) {
        const matchMinutes = details.allowedAfter.match(/PT(?:(\d+)M)?/);
        if (matchMinutes && matchMinutes[1]) {
          timeMessage = `${matchMinutes[1]} minute(s)`;
        }
      }
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Limite de commandes atteinte : Vous avez déjà passé ${details?.orders || 'plusieurs'} commande(s).`,
        `Par sécurité, merci de patienter encore ${timeMessage} avant de pouvoir commander à nouveau.`
      );
      return;
    }

    if (errorMessage === 'INSUFFICIENT_LOYALTY_POINTS') {
      const value = err.error?.details?.value;
      const info = err.error?.details?.details;
      const available: number | undefined = value?.available ?? info?.customerBalance;
      const required: number | undefined = value?.required ?? info?.requiredBalance;
      const pts = (n: number) => `${n} point${n > 1 ? 's' : ''}`;

      if (available === undefined || required === undefined) {
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          `Points de fidélité insuffisants.`,
          `Merci de retirer l'article payé en points de votre panier ou de le commander au prix normal.`
        );
        return;
      }

      const missing = Math.max(0, required - available);
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Points de fidélité insuffisants : vous avez ${pts(available)} et il en faut ${pts(required)}.`,
        missing > 0
          ? `Il vous manque ${pts(missing)}. Merci de retirer l'article payé en points de votre panier ou de le commander au prix normal.`
          : ''
      );
      return;
    }

    if (errorMessage.includes('USAGE_LIMIT_REACHED')) {
      const promoCode = errorMessage.split('/')[1] || '';
      const message = errorMessage.includes('PER_CUSTOMER')
        ? `Vous avez atteint la limite d'utilisation du code promo "${promoCode}".`
        : `Le code promo "${promoCode}" n'est plus disponible. La limite d'utilisation globale a été atteinte.`;
      this.notificationService.sendNotification(NotificationType.WARNING, message, '');
      return;
    }

    // Référence de promo invalide (règle changée côté back)
    if (errorMessage.includes('référence')) {
      const invalidRef = err.error?.details?.value;
      let promoName = '';
      if (invalidRef) {
        for (const item of this.cartService.items()) {
          if (item.type === CartItemType.PROMOTION) {
            const promoItem = item as PromotionCartItem;
            const found = [...promoItem.buyItems, ...promoItem.offeredItems]
              .find(pi => pi.rule.id === invalidRef);
            if (found) {
              promoName = promoItem.promotion.title;
              break;
            }
          }
        }
      }
      this.notificationService.sendNotification(
        NotificationType.ERROR,
        `La promotion "${promoName}" a été mise à jour. Merci de la retirer du panier et de l'ajouter à nouveau.`,
        ''
      );
      return;
    }

    // Entité introuvable (404 ou "Type/id")
    if (err.status === 404 || errorMessage.includes('/')) {
      if (errorMessage.startsWith('DeliveryCity/')) {
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          `La ville de livraison n'est plus disponible. Merci de choisir une autre ville.`,
          ''
        );
        return;
      }

      const slashIndex = errorMessage.indexOf('/');
      const entityType = slashIndex !== -1 ? errorMessage.substring(0, slashIndex).toLowerCase() : '';
      const entityId = slashIndex !== -1 ? errorMessage.substring(slashIndex + 1).trim() : '';
      const foundName = this.findEntityNameInCart(entityType, entityId);

      const typeLabels: Record<string, string> = {
        promotion: 'La promotion',
        product: 'Le produit',
        bundle: 'Le menu',
        productingredient: "L'ingrédient",
        productsupplement: 'Le supplément'
      };
      const typeLabel = typeLabels[entityType] || "L'élément";

      this.notificationService.sendNotification(
        NotificationType.WARNING,
        foundName
          ? `${typeLabel} "${foundName}" n'est plus disponible. Merci de réviser votre panier.`
          : `${typeLabel} n'est plus disponible. Merci de réviser votre panier.`,
        ''
      );
      return;
    }

    if (errorMessage) {
      this.notificationService.sendNotification(NotificationType.WARNING, errorMessage, '');
      return;
    }

    this.notificationService.errorHandler(err);
  }
  private findEntityNameInCart(entityType: string, entityId: string): string {
    if (!entityId) return '';

    for (const item of this.cartService.items()) {
      // --- A. Produit direct & sous-éléments ---
      if (item.type === CartItemType.PRODUCT) {
        const productItem = item as ProductCartItem;

        if (entityType === 'product' && productItem.product.id === entityId) {
          return productItem.product.name;
        }

        if (productItem.selectedSupplements) {
          const suppQty = productItem.selectedSupplements[entityId] || 0;
          if (suppQty > 0 || entityType === 'productsupplement') {
            const supp = productItem.product?.supplements?.find((s: any) => s.id === entityId);
            if (supp) return `${supp.name} (dans "${productItem.product.name}")`;
          }
        }

        for (const additionId of Object.keys(productItem.selectedAdditions || {})) {
          const selectedQty = productItem.selectedAdditions[additionId]?.[entityId] || 0;
          if (selectedQty > 0 || additionId === entityId) {
            const addition = productItem.product?.additions?.find((a: any) => a.id === additionId);
            const foundAcc = addition?.items?.find((i: any) => i.id === entityId);
            if (foundAcc) return `${foundAcc.name} (dans "${productItem.product.name}")`;
          }
        }

        if (entityType === 'productingredient') {
          const ing = productItem.product?.ingredients?.find((i: any) => i.id === entityId);
          if (ing) return `${ing.name} (dans "${productItem.product.name}")`;
        }
      }

      // --- B. Menu / Bundle ---
      if (item.type === CartItemType.BUNDLE) {
        const bundleItem = item as BundleCartItem;

        if (entityType === 'bundle' && bundleItem.bundle.id === entityId) {
          return bundleItem.bundle.name;
        }

        for (const customization of Object.values(bundleItem.customizedItems || {})) {
          const cust = customization as any;

          if (cust?.selectedSupplements) {
            const supp = cust?.product?.supplements?.find((s: any) => s.id === entityId);
            if (supp) return `${supp.name} (dans "${cust.product.name}" du menu "${bundleItem.bundle.name}")`;
          }

          if (cust?.product?.id === entityId) {
            return `${cust.product.name} (dans le menu "${bundleItem.bundle.name}")`;
          }

          for (const additionId of Object.keys(cust?.selectedAdditions || {})) {
            const qty = cust.selectedAdditions[additionId]?.[entityId] || 0;
            if (qty > 0) {
              const addition = cust?.product?.additions?.find((a: any) => a.id === additionId);
              const foundAcc = addition?.items?.find((i: any) => i.id === entityId);
              if (foundAcc) return `${foundAcc.name} (dans le menu "${bundleItem.bundle.name}")`;
            }
          }

          if (entityType === 'productingredient') {
            const ing = cust?.product?.ingredients?.find((i: any) => i.id === entityId);
            if (ing) return `${ing.name} (dans "${cust.product.name}" du menu "${bundleItem.bundle.name}")`;
          }
        }

        if (entityType === 'product') {
          for (const componentId of Object.keys(bundleItem.selectedItems || {})) {
            const productIds = Object.keys(bundleItem.selectedItems[componentId] || {});
            if (!productIds.includes(entityId)) continue;

            let foundName: string | null = null;
            const findProductInfo = (rules: any[]): boolean => {
              for (const rule of rules) {
                if (rule.components) {
                  for (const component of rule.components) {
                    if (component.id === componentId) {
                      const found = component.availableItems?.find((i: any) => i.id === entityId);
                      if (found) {
                        foundName = found.name;
                        return true;
                      }
                    }
                  }
                }
                if (rule.nestedRules && findProductInfo(rule.nestedRules)) return true;
              }
              return false;
            };
            if (bundleItem.bundle.selectionRules) findProductInfo(bundleItem.bundle.selectionRules);

            if (foundName) return `${foundName} (dans le menu "${bundleItem.bundle.name}")`;
          }
        }
      }

      // --- C. Promotion ---
      if (item.type === CartItemType.PROMOTION) {
        const promoItem = item as PromotionCartItem;

        if (entityType === 'promotion' && promoItem.promotion.id === entityId) {
          return promoItem.promotion.title;
        }

        // 👇 C'est ce qui manquait : produit à l'intérieur d'une promo (ton cas Coca cola)
        const foundProduct = [...(promoItem.buyItems || []), ...(promoItem.offeredItems || [])]
          .find(pi => pi.product?.id === entityId);
        if (foundProduct) {
          return `${foundProduct.product.name} (dans la promotion "${promoItem.promotion.title}")`;
        }

        for (const customization of Object.values(promoItem.customizedItems || {})) {
          const cust = customization as any;

          if (cust?.selectedSupplements) {
            const supp = cust?.product?.supplements?.find((s: any) => s.id === entityId);
            if (supp) return `${supp.name} (dans la promotion "${promoItem.promotion.title}")`;
          }

          if (cust?.product?.id === entityId) {
            return `${cust.product.name} (dans la promotion "${promoItem.promotion.title}")`;
          }

          for (const additionId of Object.keys(cust?.selectedAdditions || {})) {
            const qty = cust.selectedAdditions[additionId]?.[entityId] || 0;
            if (qty > 0) {
              const addition = cust?.product?.additions?.find((a: any) => a.id === additionId);
              const foundAcc = addition?.items?.find((i: any) => i.id === entityId);
              if (foundAcc) return `${foundAcc.name} (dans la promotion "${promoItem.promotion.title}")`;
            }
          }
        }
      }
    }

    return '';
  }
  private getOrderTypeForAPI(): string {

    const orderType = this.orderContextStore.orderType();
    switch (orderType) {
      case TypeOrderEnum.DELIVERY:
        return 'DELIVERY';
      case TypeOrderEnum.TAKEAWAY:
        return 'TAKEAWAY';
      case TypeOrderEnum.ON_SITE:
        return 'ON_SITE';
      default:
        return 'TAKEAWAY';
    }
  }

  openDialogCommand(): void {
    if (this.isCartDisabled()) {
      this.dialog.open(SharedDialogComponent, {
        width: '420px',
        panelClass: 'custom-dialog',
        data: {
          context: 'cart-disabled',
          title: 'Commandes désactivées 🛒',
          message: this.getDisabledPeriodMessage()
        }
      });
      return;
    }

    const checkoutKey = this.cartService.getOrGenerateCheckoutKey();
    console.log('Checkout Key active :', checkoutKey);

    const proceedWithCartSave = () => {
      // 1. Récupération directe de l'état actuel du panier serveur
      this.cartService.getCart().pipe(
        catchError(() => of(null))
      ).subscribe((res) => {
        const serverPayload = res?.body?.data?.payload;
        const isSynced = !!serverPayload && this.cartService.isCartSyncedWithServer(serverPayload);

        // ✅ Si le panier local est déjà 100% identique au serveur -> Pas de POST, navigation directe
        if (isSynced) {
          this.router.navigate(['/command']);
          this.toggleCart();
          return;
        }

        // ❌ Si différent ou non synchronisé -> Appel POST de sauvegarde
        const orderType = this.getOrderTypeForAPI();
        this.cartService.saveCartToServer(orderType).subscribe({
          next: () => {
            this.router.navigate(['/command']);
            this.toggleCart();
          },
          error: (err) => {
            console.error('Erreur lors de la sauvegarde du panier :', err);
            this.handleCartSaveError(err);
          }
        });
      });
    };

    if (!this.authenticationService.isLogin()) {
      const dialogRef = this.dialog.open(LoginComponent, {
        width: '700px',
        panelClass: 'custom-dialog'
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result === true) {
          proceedWithCartSave();
        }
      });
      return;
    }

    proceedWithCartSave();
  }

  // openDialogCommand(): void {
  //   if (this.isCartDisabled()) {
  //     this.dialog.open(SharedDialogComponent, {
  //       width: '420px',
  //       panelClass: 'custom-dialog',
  //       data: {
  //         context: 'cart-disabled',
  //         title: 'Commandes désactivées 🛒',
  //         message: this.getDisabledPeriodMessage()
  //       }
  //     });
  //     return;
  //   }
  //
  //   const checkoutKey = this.cartService.getOrGenerateCheckoutKey();
  //   console.log('Checkout Key active :', checkoutKey);
  //
  //   const proceedWithCartSave = () => {
  //     if (this.serverCartSynced()) {
  //       this.router.navigate(['/command']);
  //       this.toggleCart();
  //       return;
  //     }
  //     const orderType = this.getOrderTypeForAPI();
  //
  //     this.cartService.saveCartToServer(orderType).subscribe({
  //       next: () => {
  //         this.router.navigate(['/command']);
  //         this.toggleCart();
  //       },
  //       error: (err) => {
  //         console.error('Erreur lors de la sauvegarde du panier :', err);
  //         this.handleCartSaveError(err);
  //       }
  //
  //     });
  //   };
  //
  //   if (!this.authenticationService.isLogin()) {
  //     const dialogRef = this.dialog.open(LoginComponent, {
  //       width: '700px',
  //       panelClass: 'custom-dialog'
  //     });
  //     dialogRef.afterClosed().subscribe((result) => {
  //       if (result === true) {
  //         proceedWithCartSave();
  //       }
  //     });
  //     return;
  //   }
  //
  //   proceedWithCartSave();
  // }

  isFixedAmountOrderOffer(item: CartItem): boolean {
    if (item.type !== CartItemType.PROMOTION) return false;
    const promoItem = item as PromotionCartItem;
    if (promoItem.promotion?.type !== 'order-offer') return false;
    return promoItem.promotion.payload?.reduction?.type === 'FIXED_AMOUNT';
  }

  isRedeemItem(item: CartItem): boolean {
    return (item as ProductCartItem | BundleCartItem)?.redeemWithPoints === true;
  }

  getItemPointsCost(item: CartItem): number {
    return (item as ProductCartItem | BundleCartItem)?.pointsCost ?? 0;
  }

  getSetPriceValue(item: CartItem): number {
    if (item.type !== CartItemType.PROMOTION) return 0;
    const promoItem = item as PromotionCartItem;
    return promoItem.promotion.payload?.reduction?.value || 0;
  }

  getOrderOfferTotalBase(item: CartItem): number {
    if (item.type !== CartItemType.PROMOTION) return 0;
    const promoItem = item as PromotionCartItem;
    return promoItem.offeredItems.reduce((sum, offered) => {
      return this.truncate2(sum + (offered.unitBasePrice || 0) * (offered.quantity || 1));
    }, 0);
  }

  getItemDisplayTotal(item: CartItem): number {
    if (item.type === CartItemType.PRODUCT) {
      const productItem = item as ProductCartItem;
      const reduction = (productItem as any).reduction;
      if (reduction?.type === 'SET_PRICE') {
        let supplementsTotal = 0;
        Object.keys(productItem.selectedSupplements || {}).forEach(suppId => {
          const supplement = productItem.product.supplements?.find((s: any) => s.id === suppId);
          if (supplement) {
            const qty = productItem.selectedSupplements[suppId] || 1;
            let price = supplement.price || 0;
            if (productItem.selectedSize && supplement.pricePerSizes?.length > 0) {
              const priced = supplement.pricePerSizes.find((ps: any) => ps.size.id === productItem.selectedSize);
              if (priced?.price) price = priced.price;
            }
            supplementsTotal += this.truncate2(price * qty);
          }
        });
        const totalPourUn = reduction.value + supplementsTotal;
        return this.truncate2(totalPourUn * item.quantity);
      }
    }

    if (
      item.type === CartItemType.PROMOTION &&
      this.isOrderOffer(item)
    ) {
      const promoItem = item as PromotionCartItem;
      const reduction = promoItem.promotion.payload?.reduction;
      if (reduction?.type === 'SET_PRICE') {
        const supplementsTotal = promoItem.offeredItems.reduce((sum, offered) => {
          return this.truncate2(sum + this.getTotalSupplementsPrice(offered));
        }, 0);
        return this.truncate2(reduction.value + supplementsTotal);
      }
    }
    return item.total;
  }

  getOrderOfferFixedDiscount(item: CartItem): number {
    if (item.type !== CartItemType.PROMOTION) return 0;
    const promoItem = item as PromotionCartItem;
    const reduction = promoItem.promotion.payload?.reduction;
    if (reduction?.type === 'FIXED_AMOUNT') {
      return this.truncate2(reduction.value || 0);
    }
    if (reduction?.type === 'SET_PRICE') {
      return this.truncate2(reduction.value || 0);
    }
    const totalBase = promoItem.offeredItems.reduce((sum, offered) =>
      this.truncate2(sum + (offered.unitBasePrice || 0) * (offered.quantity || 1)), 0);
    const totalAfterDiscount = promoItem.offeredItems.reduce((sum, offered) => {
      const priceWithoutSupp = (offered.unitPrice || 0) - (offered.unitSupplementsPrice || 0);
      return this.truncate2(sum + priceWithoutSupp * (offered.quantity || 1));
    }, 0);
    return this.truncate2(totalBase - totalAfterDiscount);
  }

  getTotalSupplementsPrice(offered: any): number {
    return Math.floor(
      (offered.selectedSupplements || []).reduce((sum: number, sup: any) => {
        let price = sup.price || 0;

        if ((!price || price === 0) && sup.pricePerSizes?.length > 0) {
          const sizeName = offered.rule?.sizes?.[0]?.name;
          if (sizeName) {
            const found = sup.pricePerSizes.find((ps: any) => ps.size?.name === sizeName);
            if (found?.price) price = found.price;
          } else {
            price = sup.pricePerSizes[0]?.price || 0;
          }
        }

        return sum + (price * (sup.quantity || 1));
      }, 0) * 100
    ) / 100;
  }

  isSetPriceReduction(item: CartItem): boolean {
    if (item.type !== CartItemType.PROMOTION) return false;
    const promoItem = item as PromotionCartItem;
    const reduction = promoItem.promotion.payload?.reduction;
    return reduction?.type === 'SET_PRICE';
  }

  getOrderOfferReductionBadge(item: CartItem): string {
    if (item.type !== CartItemType.PROMOTION) return '';
    const promoItem = item as PromotionCartItem;
    const reduction = promoItem.promotion.payload?.reduction;

    if (!reduction) return '100% Offert';

    if (reduction.type === 'FIXED_AMOUNT') {
      return `-${this.truncate2(reduction.value || 0)}€`;
    }

    if (reduction.type === 'PERCENTAGE') {
      const pct = Math.round((reduction.value || 0) * 100);
      return pct === 100 ? 'Gratuit 🎁' : `-${pct}%`;
    }

    return '🎁 Offert';
  }

  isOrderOffer(item: CartItem): boolean {
    if (item.type !== CartItemType.PROMOTION) return false;
    return (item as PromotionCartItem).promotion?.type === 'order-offer';
  }

  isPromoCodeProductOffer(item: CartItem): boolean {
    if (item.type !== CartItemType.PROMOTION) return false;
    const promoItem = item as PromotionCartItem;
    return promoItem.promotion?.type === 'product-offer'
      && promoItem.promotion?.addedByPromoCode === true;
  }

  getOfferedItems(item: CartItem): any[] {
    if (item.type !== CartItemType.PROMOTION) return [];
    return (item as PromotionCartItem).offeredItems || [];
  }

  incrementQuantity(itemId: string): void {
    const item = this.cartService.getItem(itemId);
    if (!item || (item as any).redeemWithPoints === true) return;

    if (item) {
      this.cartService.updateItemQuantity(itemId, item.quantity + 1);
    }
  }

  decrementQuantity(itemId: string): void {
    const item = this.cartService.getItem(itemId);
    if (!item || (item as any).redeemWithPoints === true) return;

    if (!item) return;
    if (item?.quantity === 1) {
      const dialogRef = this.dialog.open(SharedDialogComponent, {
        panelClass: 'custom-dialog',
        disableClose: true,
        data: {context: 'item'}
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result === true) this.cartService.removeItem(itemId);
      });
    } else {
      this.cartService.updateItemQuantity(itemId, item.quantity - 1);
    }
  }

  removeItem(itemId: string): void {
    const dialogRef = this.dialog.open(SharedDialogComponent, {
      panelClass: 'custom-dialog',
      disableClose: true,
      data: {context: 'item'}
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) this.cartService.removeItem(itemId);
    });
  }

  clearCart(): void {
    const dialogRef = this.dialog.open(SharedDialogComponent, {
      panelClass: 'custom-dialog',
      disableClose: true,
      data: {context: 'shopping'}
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        // 1. Si l'utilisateur est connecté, vider le panier côté serveur via l'API
        if (this.authenticationService.isLogin()) {
          this.cartService.clearCartOnServer().subscribe({
            next: () => {
              this.handleLocalClearCart();
            },
            error: (err) => {
              console.error('Erreur lors du vidage du panier sur le serveur:', err);
              // On vide le panier localement quand même ou on gère l'erreur
              this.handleLocalClearCart();
              this.notificationService.errorHandler(err);
            }
          });
        } else {
          // 2. Utilisateur non connecté -> Réinitialisation locale uniquement
          this.handleLocalClearCart();
        }
      }
    });
  }

  private handleLocalClearCart(): void {
    this.cartService.clearCart();
    this.orderContextStore.reset();
    this.serverCartSynced.set(false);
    if (this.isCartSlideOpen) {
      this.toggleCart();
    }
  }

  isPromoCodeProduct(item: CartItem): boolean {
    if (item.type !== CartItemType.PRODUCT) return false;
    return (item as any).addedByPromoCode === true;
  }

  canEditProduct(item: CartItem): boolean {
    if (item.type !== CartItemType.PRODUCT) return false;
    const productItem = item as ProductCartItem;
    const product = productItem.product;

    const hasCustomization =
      (product.containAdditions === true) ||
      (product.containSupplements === true) ||
      (product.ingredients?.length > 0) ||
      (product.supplements?.length > 0) ||
      (product.additions?.length > 0);
    return hasCustomization;
  }

  getProductOriginalPrice(item: ProductCartItem): number {
    if (item.product.pricePerSizes?.length > 0 && item.selectedSize) {
      const sizeObj = item.product.pricePerSizes.find(
        (pps: any) => pps.size.id === item.selectedSize
      );
      if (sizeObj?.price) return sizeObj.price;
      return item.product.pricePerSizes[0]?.price || item.product.price || 0;
    }
    return item.product.price || 0;
  }

  getDiscountedTotalPrice(item: ProductCartItem): number {
    const originalTotal = this.getProductOriginalPrice(item) * item.quantity;
    const reduction = (item as any).reduction;

    if (!reduction) return originalTotal;
    if (reduction.type === 'SET_PRICE') {
      return this.truncate2(reduction.value * item.quantity);
    }
    if (reduction.type === 'FIXED_AMOUNT') {
      return this.truncate2(Math.max(0, originalTotal - (reduction.value * item.quantity)));
    }

    if (reduction.type === 'PERCENTAGE') {
      return this.truncate2(originalTotal * (1 - reduction.value));
    }

    return originalTotal;
  }

  hasProductDiscount(item: ProductCartItem): boolean {
    const original = this.getProductOriginalPrice(item);
    return original > 0 && item.selectedPrice < original - 0.001;
  }

  getProductDiscountBadge(item: ProductCartItem): string {
    const original = this.getProductOriginalPrice(item);
    if (!original || !this.hasProductDiscount(item)) return '';

    const reduction = (item as any).reduction;

    if (reduction?.type === 'PERCENTAGE') {
      return `-${Math.round(reduction.value * 100)}%`;
    }

    if (reduction?.type === 'FIXED_AMOUNT') {
      return `-${this.truncate2(reduction.value)}€`;
    }

    if (reduction?.type === 'SET_PRICE') {
      return 'Prix fixe';
    }

    const diff = original - item.selectedPrice;
    return `-${this.truncate2(diff)}€`;
  }

  getItemTitle(item: CartItem): string {
    switch (item.type) {
      case CartItemType.PRODUCT: {
        const productItem = item as ProductCartItem;
        let title = productItem.product.name;
        if (productItem.selectedSize) {
          const sizeName = this.getSizeNameFromId(productItem.selectedSize, productItem.product);
          title += ` (${sizeName})`;
        }
        return title;
      }
      case CartItemType.BUNDLE:
        return (item as BundleCartItem).bundle.name;
      case CartItemType.PROMOTION:
        return (item as PromotionCartItem).promotion.title;
      default:
        return 'Article';
    }
  }

  private getSizeNameFromId(sizeId: string, product: any): string | null {
    if (product.pricePerSizes?.length > 0) {
      const sizeObj = product.pricePerSizes.find((pps: any) => pps.size.id === sizeId);
      if (sizeObj?.size?.name) return sizeObj.size.name;
    }
    if (product.sizes?.length > 0) {
      const size = product.sizes.find((s: any) => s.id === sizeId);
      if (size?.name) return size.name;
    }
    return null;
  }

  truncate2(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  // truncate2(value: number): number {
  //   return Math.floor(value * 100) / 100;
  // }

  getBundleCompositionWithCustomizations(item: CartItem): string {
    if (item?.type !== CartItemType.BUNDLE) return '';
    const bundleItem = item as BundleCartItem;
    if (!bundleItem?.selectedItems || typeof bundleItem.selectedItems !== 'object') return '';

    const lines: string[] = [];

    Object.keys(bundleItem.selectedItems).forEach(componentId => {
      const componentItems = bundleItem.selectedItems[componentId];
      if (!componentItems || typeof componentItems !== 'object') return;

      Object.keys(componentItems).forEach(productId => {
        const quantity = componentItems[productId] || 0;

        for (let index = 0; index < quantity; index++) {
          let productName = 'Produit inconnu';
          let sizeName = '';

          const findProductInfo = (rules: any[]): boolean => {
            if (!Array.isArray(rules)) return false;

            for (const rule of rules) {
              if (Array.isArray(rule?.components)) {
                for (const component of rule.components) {
                  if (component?.id === componentId) {
                    if (component.size?.name) sizeName = component.size.name;

                    if (Array.isArray(component.availableItems)) {
                      const found = component.availableItems.find((i: any) => i?.id === productId);
                      if (found) {
                        productName = found.name;
                        return true;
                      }
                    }
                  }
                }
              }
              if (Array.isArray(rule?.nestedRules) && findProductInfo(rule.nestedRules)) {
                return true;
              }
            }
            return false;
          };

          if (Array.isArray(bundleItem.bundle?.selectionRules)) {
            findProductInfo(bundleItem.bundle.selectionRules);
          }

          const instanceLabel = quantity > 1 ? ` #${index + 1}` : '';
          const customKey = `${componentId}_${productId}_${index}`;
          const customization = bundleItem.customizedItems?.[customKey];
          let hasCustomizations = false;
          const customizationLines: string[] = [];

          if (customization) {
            // 1. Ingrédients exclus (Sans)
            if (Array.isArray(customization.excludedIngredients) && customization.excludedIngredients.length > 0) {
              const ingredients = customization.product?.ingredients;
              const excluded = customization.excludedIngredients
                .map((id: string) => {
                  const ing = Array.isArray(ingredients) ? ingredients.find((i: any) => i?.id === id) : null;
                  return ing?.name;
                })
                .filter(Boolean)
                .join(', ');

              if (excluded) {
                customizationLines.push(`Sans : ${excluded}`);
                hasCustomizations = true;
              }
            }

            // 2. Accompagnements
            if (customization.selectedAdditions && typeof customization.selectedAdditions === 'object') {
              const parts: string[] = [];
              Object.keys(customization.selectedAdditions).forEach(addId => {
                const additions = customization.product?.additions;
                const addition = Array.isArray(additions) ? additions.find((a: any) => a?.id === addId) : null;

                if (addition && Array.isArray(addition.items) && customization.selectedAdditions[addId]) {
                  Object.keys(customization.selectedAdditions[addId]).forEach(itemId => {
                    const qty = customization.selectedAdditions[addId][itemId];
                    if (qty > 0) {
                      const it = addition.items.find((i: any) => i?.id === itemId);
                      if (it) {
                        const qtyText = qty > 1 ? ` x${qty}` : '';
                        parts.push(`${it.name}${qtyText}`);
                      }
                    }
                  });
                }
              });

              if (parts.length > 0) {
                customizationLines.push(`Accompagnements: ${parts.join(', ')}`);
                hasCustomizations = true;
              }
            }

            // 3. Suppléments
            if (customization.selectedSupplements && typeof customization.selectedSupplements === 'object') {
              const parts: string[] = [];
              let totalPrice = 0;

              Object.keys(customization.selectedSupplements).forEach(suppId => {
                const supplements = customization.product?.supplements;
                const supplement = Array.isArray(supplements) ? supplements.find((s: any) => s?.id === suppId) : null;

                if (supplement) {
                  const qty = customization.selectedSupplements[suppId] || 1;
                  let unitPrice = supplement.price || 0;

                  if (customization.selectedSize && Array.isArray(supplement.pricePerSizes) && supplement.pricePerSizes.length > 0) {
                    const priced = supplement.pricePerSizes.find((ps: any) => ps?.size?.name === customization.selectedSize);
                    if (priced?.price) unitPrice = priced.price;
                  }

                  const itemTotal = unitPrice * qty;
                  totalPrice += itemTotal;
                  const qtyText = qty > 1 ? ` x${qty}` : '';
                  const priceText = itemTotal > 0 ? ` (+${this.truncate2(itemTotal).toFixed(2).replace('.', ',')} €)` : '';
                  const category = supplement.categories?.[0] || '';
                  parts.push(`[CAT]${category} - ${supplement.name}${qtyText}${priceText}`);
                }
              });

              if (parts.length > 0) {
                const totalText = totalPrice > 0 ? ` (${this.truncate2(totalPrice).toFixed(2).replace('.', ',')} €)` : '';
                customizationLines.push(`Suppléments${totalText}: ${parts.join('|||')}`);
                hasCustomizations = true;
              }
            }
          }

          let productLine = `[PRODUCT]${productName}`;
          if (sizeName) productLine += ` (${sizeName})`;
          productLine += `${instanceLabel}`;
          if (hasCustomizations) productLine += ':';

          lines.push(productLine);
          if (customizationLines.length > 0) lines.push(...customizationLines);
          lines.push('');
        }
      });
    });

    while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
    return lines.join('\n');
  }

  getProductCustomizationText(item: CartItem): string {
    if (item?.type !== CartItemType.PRODUCT) return '';
    const productItem = item as ProductCartItem;
    if (!productItem?.product) return '';

    const lines: string[] = [];

    // Ingrédients exclus
    if (Array.isArray(productItem.excludedIngredients) && productItem.excludedIngredients.length > 0) {
      const excludedNames = productItem.excludedIngredients
        .map(id => {
          const ing = Array.isArray(productItem.product.ingredients)
            ? productItem.product.ingredients.find((i: any) => i.id === id)
            : null;
          return ing?.name;
        })
        .filter(Boolean)
        .join(', ');

      if (excludedNames) lines.push(`Sans: ${excludedNames}`);
    }

    // Accompagnements / Additions
    if (Array.isArray(productItem.product.additions) && productItem.product.additions.length > 0) {
      productItem.product.additions.forEach((addition: any) => {
        if (!addition || !Array.isArray(addition.items)) return;

        const selectedItems: string[] = [];
        const additionSize = addition.itemsSize?.name ? ` (Taille ${addition.itemsSize.name})` : '';

        addition.items.forEach((item: any) => {
          const qty = productItem.selectedAdditions?.[addition.id]?.[item.id] || 0;
          if (qty > 0) selectedItems.push(`${item.name} x${qty} ${additionSize}`);
        });

        if (selectedItems.length > 0) {
          const categoryName = this.getAdditionCategoryName(addition);
          lines.push(`${categoryName}: ${selectedItems.join(', ')}`);
        }
      });
    }

    return lines.join('\n');
  }

  private getAdditionCategoryName(addition: any): string {
    if (addition.category?.name) return addition.category.name;
    if (addition.categories?.length > 0) return addition.categories[0];
    return 'Accompagnement';
  }

  getPromotionCompositionText(item: CartItem): string {
    if (item?.type !== CartItemType.PROMOTION) return '';
    const promoItem = item as PromotionCartItem;
    if (!promoItem) return '';

    const lines: string[] = [];
    const isRedeem = promoItem.redeemWithPoints === true;
    const isInverted = promoItem.promotion?.payload?.invertedDueToCheapestOffered || false;
    if (isInverted) lines.push('');

    const getSizesText = (sizes?: any[]): string => {
      if (!Array.isArray(sizes) || sizes.length === 0) return '';
      return sizes.map(s => s?.name).filter(Boolean).join(', ');
    };

    // Produits Achetés
    if (Array.isArray(promoItem.buyItems) && promoItem.buyItems.length > 0) {
      if (!isRedeem) {
        lines.push('📝 Produits Achetés');
        lines.push('');
      }
      promoItem.buyItems.forEach((buy) => {
        if (!buy?.product) return;
        const sizeText = getSizesText(buy.rule?.sizes);
        const unitBasePrice = buy.unitBasePrice || 0;

        for (let i = 0; i < (buy.quantity || 1); i++) {
          const instanceLabel = buy.quantity > 1 ? ` #${i + 1}` : '';

          if (isRedeem) {
            lines.push(`[PRODUCT]${buy.product.name}${sizeText ? ` (${sizeText})` : ''}${instanceLabel}`);
          } else {
            let priceLine = `${buy.product.name}`;
            if (sizeText) priceLine += ` (${sizeText})`;
            priceLine += `${instanceLabel}: `;
            if (unitBasePrice > 0) priceLine += `${this.truncate2(unitBasePrice)}€`;
            lines.push(`${priceLine}`);
          }

          const customKey = `${buy.rule?.id}_${buy.product.id}_${i}`;
          const customization = promoItem.customizedItems?.[customKey];
          if (customization) {
            if (Array.isArray(customization.excludedIngredients) && customization.excludedIngredients.length > 0) {
              const excluded = customization.excludedIngredients
                .map((id: string) => customization.product?.ingredients?.find((ing: any) => ing.id === id)?.name)
                .filter(Boolean).join(', ');
              if (excluded) lines.push(`Sans : ${excluded}`);
            }

            if (customization.selectedAdditions && typeof customization.selectedAdditions === 'object') {
              const parts: string[] = [];
              Object.keys(customization.selectedAdditions).forEach(addId => {
                const addition = customization.product?.additions?.find((a: any) => a.id === addId);
                if (addition && Array.isArray(addition.items)) {
                  Object.keys(customization.selectedAdditions[addId] || {}).forEach(itemId => {
                    const qty = customization.selectedAdditions[addId][itemId];
                    if (qty > 0) {
                      const addItem = addition.items.find((i: any) => i.id === itemId);
                      if (addItem) parts.push(`${addItem.name}${qty > 1 ? ` x${qty}` : ''}`);
                    }
                  });
                }
              });
              if (parts.length > 0) lines.push(`Accompagnements : ${parts.join(', ')}`);
            }

            if (customization.selectedSupplements && typeof customization.selectedSupplements === 'object') {
              const parts: string[] = [];
              let totalPrice = 0;
              Object.keys(customization.selectedSupplements).forEach(suppId => {
                const supplement = customization.product?.supplements?.find((s: any) => s.id === suppId);
                if (supplement) {
                  const qty = customization.selectedSupplements[suppId] || 1;
                  let unitPrice = supplement.price || 0;
                  if (customization.selectedSize && Array.isArray(supplement.pricePerSizes)) {
                    const priced = supplement.pricePerSizes.find((ps: any) => ps.size?.name === customization.selectedSize);
                    if (priced) unitPrice = priced.price;
                  }
                  const itemTotal = unitPrice * qty;
                  totalPrice += itemTotal;
                  const priceText = itemTotal > 0 ? ` (+${this.truncate2(itemTotal).toFixed(2).replace('.', ',')} €)` : '';
                  const category = supplement.categories?.[0] || '';
                  parts.push(`[CAT]${category}-${supplement.name}${qty > 1 ? ` x${qty}` : ''}${priceText}`);
                }
              });
              const totalText = totalPrice > 0 ? ` (${this.truncate2(totalPrice).toFixed(2).replace('.', ',')} €)` : '';
              lines.push(`Suppléments${totalText}: ${parts.join('|||')}`);
            }
          }
          lines.push('');
        }
      });
    }

    // Produits Offerts
    if (Array.isArray(promoItem.offeredItems) && promoItem.offeredItems.length > 0) {
      if (!isRedeem) {
        lines.push('📝 Produits Offerts');
        lines.push('');
      }
      promoItem.offeredItems.forEach((offered) => {
        if (!offered?.product) return;
        const sizeText = getSizesText(offered.rule?.sizes);
        const unitBasePrice = offered.unitBasePrice || 0;

        for (let i = 0; i < (offered.quantity || 1); i++) {
          const instanceLabel = offered.quantity > 1 ? ` #${i + 1}` : '';

          if (isRedeem) {
            lines.push(`[PRODUCT]${offered.product.name}${sizeText ? ` (${sizeText})` : ''}${instanceLabel}`);
          } else {
            let priceLine = `${offered.product.name}`;
            if (sizeText) priceLine += ` (${sizeText})`;
            priceLine += `${instanceLabel}: `;

            const reductionType = promoItem.promotion?.payload?.reduction?.type;
            const reductionValue = promoItem.promotion?.payload?.reduction?.value;

            if (reductionType === 'SET_PRICE' && reductionValue !== undefined) {
              priceLine += `${this.truncate2(reductionValue)}€`;
            } else if (unitBasePrice > 0) {
              priceLine += `${this.truncate2(unitBasePrice)}€`;
            }
            lines.push(`${priceLine}`);
          }

          const customKey = `${offered.rule?.id}_${offered.product.id}_${i}`;
          const customization = promoItem.customizedItems?.[customKey];
          if (customization) {
            if (Array.isArray(customization.excludedIngredients) && customization.excludedIngredients.length > 0) {
              const excluded = customization.excludedIngredients
                .map((id: string) => customization.product?.ingredients?.find((ing: any) => ing.id === id)?.name)
                .filter(Boolean).join(', ');
              if (excluded) lines.push(`Sans : ${excluded}`);
            }

            if (customization.selectedAdditions && typeof customization.selectedAdditions === 'object') {
              const parts: string[] = [];
              Object.keys(customization.selectedAdditions).forEach(addId => {
                const addition = customization.product?.additions?.find((a: any) => a.id === addId);
                if (addition && Array.isArray(addition.items)) {
                  Object.keys(customization.selectedAdditions[addId] || {}).forEach(itemId => {
                    const qty = customization.selectedAdditions[addId][itemId];
                    if (qty > 0) {
                      const addItem = addition.items.find((i: any) => i.id === itemId);
                      if (addItem) parts.push(`${addItem.name}${qty > 1 ? ` x${qty}` : ''}`);
                    }
                  });
                }
              });
              if (parts.length > 0) lines.push(`Accompagnements : ${parts.join(', ')}`);
            }

            if (customization.selectedSupplements && typeof customization.selectedSupplements === 'object') {
              const parts: string[] = [];
              let totalPrice = 0;
              Object.keys(customization.selectedSupplements).forEach(suppId => {
                const supplement = customization.product?.supplements?.find((s: any) => s.id === suppId);
                if (supplement) {
                  const qty = customization.selectedSupplements[suppId] || 1;
                  let unitPrice = supplement.price || 0;
                  if (customization.selectedSize && Array.isArray(supplement.pricePerSizes)) {
                    const priced = supplement.pricePerSizes.find((ps: any) => ps.size?.name === customization.selectedSize);
                    if (priced) unitPrice = priced.price;
                  }
                  const itemTotal = unitPrice * qty;
                  totalPrice += itemTotal;
                  const priceText = itemTotal > 0 ? ` (+${this.truncate2(itemTotal).toFixed(2).replace('.', ',')} €)` : '';
                  const category = supplement.categories?.[0] || '';
                  parts.push(`[CAT]${category}-${supplement.name}${qty > 1 ? ` x${qty}` : ''}${priceText}`);
                }
              });
              const totalText = totalPrice > 0 ? ` (${this.truncate2(totalPrice).toFixed(2).replace('.', ',')} €)` : '';
              lines.push(`Suppléments${totalText}: ${parts.join('|||')}`);
            }
          }
          lines.push('');
        }
      });
    }

    while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
    return lines.join('\n');
  }

  getSupPrice(sup: any, offered: any): number {
    if (sup.price && sup.price > 0) return sup.price;

    if (sup.pricePerSizes?.length > 0) {
      const sizeName = offered.rule?.sizes?.[0]?.name;
      if (sizeName) {
        const found = sup.pricePerSizes.find((ps: any) => ps.size?.name === sizeName);
        if (found?.price) return found.price;
      }
      return sup.pricePerSizes[0]?.price || 0;
    }

    return 0;
  }

  getPercentageValue(item: CartItem): number {
    if (item.type !== CartItemType.PROMOTION) return 0;
    return this.truncate2((item.promotion.payload?.reduction?.value || 0) * 100);
  }

  getReductionType(item: CartItem): string {
    if (item.type !== CartItemType.PROMOTION) return '';
    return item.promotion.payload?.reduction?.type || '';
  }

  getFixedAmountValue(item: CartItem): string {
    if (item.type !== CartItemType.PROMOTION) return '0';
    const reduction = item.promotion.payload?.reduction;
    if (reduction?.type === 'FIXED_AMOUNT') return `Réduction -${reduction.value}`;
    return '0';
  }

  calculateTotalOfferedPrice(item: CartItem): string {
    if (item.type !== CartItemType.PROMOTION) return '0.00';
    const promoItem = item as PromotionCartItem;
    let total = 0;
    promoItem.offeredItems.forEach(offered => {
      total += (offered.unitBasePrice || 0) * offered.quantity;
    });
    return `${this.truncate2(total)}`;
  }

  calculateFinalOfferedPrice(item: CartItem): string {
    if (item.type !== CartItemType.PROMOTION) return '0.00';
    const promoItem = item as PromotionCartItem;
    const reduction = promoItem.promotion.payload?.reduction;
    if (!reduction || reduction.type !== 'FIXED_AMOUNT') return this.calculateTotalOfferedPrice(item);
    let total = 0;
    promoItem.offeredItems.forEach(offered => {
      total += (offered.unitBasePrice || 0) * offered.quantity;
    });
    return `${this.truncate2(Math.max(0, total - (reduction.value || 0)))}`;
  }

  extractPriceFromLine(line: string): number {
    const match = line.match(/: (\d+\.?\d*)€/);
    return match ? parseFloat(match[1]) : 0;
  }

  getOriginalPrice(line: string): string {
    return `${this.truncate2(this.extractPriceFromLine(line))}`;
  }

  calculateDiscountedPrice(line: string, item: CartItem): string {
    if (item.type !== CartItemType.PROMOTION) return `${this.truncate2(this.extractPriceFromLine(line))}`;
    const promoItem = item as PromotionCartItem;
    const originalPrice = this.extractPriceFromLine(line);
    const reduction = promoItem.promotion.payload?.reduction;
    if (!reduction) return `${this.truncate2(originalPrice)}`;
    if (reduction.type === 'FIXED_AMOUNT') return `${(originalPrice)}`;
    if (reduction.type === 'PERCENTAGE')
      return `${Math.round(originalPrice * (1 - (reduction.value || 0)) * 100) / 100}`;
    return `${(originalPrice)}`;
  }

  getPriceFromLine(line: string): string {
    return this.getOriginalPrice(line);
  }

  isProductOffered(line: string, item: CartItem): boolean {
    if (item.type !== CartItemType.PROMOTION) return false;
    if (!line.includes(':') || !line.includes('€')) return false;
    const lines = this.getPromotionCompositionText(item).split('\n');
    let currentLineIndex = -1;
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].trim() === line.trim()) {
        currentLineIndex = i;
        break;
      }
    }
    if (currentLineIndex === -1) return false;
    let isInOfferedSection = false;
    for (let i = 0; i <= currentLineIndex; i++) {
      const l = lines[i].trim();
      if (l.startsWith('📝 Produits Offerts')) isInOfferedSection = true;
      else if (l.startsWith('📝 Produits Achetés')) isInOfferedSection = false;
    }
    return isInOfferedSection;
  }

  isMinimumOrderReached(): boolean {
    const context = this.orderContextStore.getOrderContext();
    const totalPrice = this.cartService.totalPrice();
    if (!context.minOrderAmounts || !Array.isArray(context.minOrderAmounts) || context.minOrderAmounts.length === 0) return true;
    return totalPrice >= context.minOrderAmounts[0].amount;
  }

  getMinimumOrderAmount(): number {
    const context = this.orderContextStore.getOrderContext();
    if (!context.minOrderAmounts || !Array.isArray(context.minOrderAmounts) || context.minOrderAmounts.length === 0) return 0;
    return context.minOrderAmounts[0].amount;
  }

  getMinimumOrderWarning(): string {
    if (this.isMinimumOrderReached()) return '';
    const minAmount = this.getMinimumOrderAmount();
    const missingAmount = minAmount - this.cartService.totalPrice();
    return `Minimum Commande ${minAmount.toFixed(2)}€ non atteint! (${missingAmount.toFixed(2)}€ manquant${missingAmount > 1 ? 's' : ''})`;
  }

  openPromoCodeDialog(): void {
    this.dialog.open(PromoCodeDialogComponent, {
      disableClose: true,
      panelClass: 'custom-dialog',
      width: '420px',
    });
  }

  getProductSupplementsStructured(item: CartItem): { category: string; name: string; priceText: string }[] {
    if (item?.type !== CartItemType.PRODUCT) return [];
    const productItem = item as ProductCartItem;
    if (!productItem?.selectedSupplements || typeof productItem.selectedSupplements !== 'object') return [];

    const result: { category: string; name: string; priceText: string }[] = [];

    Object.keys(productItem.selectedSupplements).forEach(suppId => {
      const supplements = productItem.product?.supplements;
      const supplement = Array.isArray(supplements) ? supplements.find((s: any) => s.id === suppId) : null;

      if (supplement) {
        const qty = productItem.selectedSupplements[suppId] || 1;
        let price = supplement.price || 0;

        if (productItem.selectedSize && Array.isArray(supplement.pricePerSizes) && supplement.pricePerSizes.length > 0) {
          const priced = supplement.pricePerSizes.find((ps: any) => ps.size?.id === productItem.selectedSize);
          if (priced?.price) price = priced.price;
        }

        const itemTotal = this.truncate2(price * qty);
        const category = supplement.categories?.[0] || '';
        const qtyText = qty > 1 ? ` x${qty}` : '';
        const priceText = itemTotal > 0 ? `(+${itemTotal.toFixed(2)}€)` : '';
        result.push({category, name: supplement.name + qtyText, priceText});
      }
    });

    return result;
  }

  getProductSupplemenstTotalPrice(item: CartItem): string {
    if (item?.type !== CartItemType.PRODUCT) return '';
    const productItem = item as ProductCartItem;
    if (!productItem?.selectedSupplements || typeof productItem.selectedSupplements !== 'object') return '';

    let total = 0;
    Object.keys(productItem.selectedSupplements).forEach(suppId => {
      const supplements = productItem.product?.supplements;
      const supplement = Array.isArray(supplements) ? supplements.find((s: any) => s.id === suppId) : null;

      if (supplement) {
        const qty = productItem.selectedSupplements[suppId] || 1;
        let price = supplement.price || 0;

        if (productItem.selectedSize && Array.isArray(supplement.pricePerSizes) && supplement.pricePerSizes.length > 0) {
          const priced = supplement.pricePerSizes.find((ps: any) => ps.size?.id === productItem.selectedSize);
          if (priced?.price) price = priced.price;
        }

        total += this.truncate2(price * qty);
      }
    });

    return total > 0 ? `(${this.truncate2(total).toFixed(2).replace('.', ',')} €)` : '';
  }

  editCartItem(item: CartItem): void {
    if (item.type !== CartItemType.PRODUCT) return;
    const productItem = item as ProductCartItem;
    let sizeName: string | null = null;
    let sizePrice: number = productItem.product.price || 0;

    if (productItem.selectedSize && productItem.product.pricePerSizes?.length > 0) {
      const sizeObj = productItem.product.pricePerSizes.find(
        (pps: any) => pps.size.id === productItem.selectedSize
      );
      if (sizeObj) {
        sizeName = sizeObj.size.name;
        sizePrice = sizeObj.price;
      }
    }
    const params: any = {};

    if (sizeName) params['size'] = sizeName;
    if (productItem.redeemWithPoints) params['mode'] = 'redeem';
    if (this.isCartSlideOpen) {
      this.toggleCart();
    }
    this.router.navigate(['/product', productItem.product.slug], {
      queryParams: params,
      state: {
        productId: productItem.product.id,
        selectedSize: sizeName,
        selectedPrice: sizePrice,
        editingCartItemId: productItem.id,
        existingSelectedIngredients: productItem.selectedIngredients || [],
        existingExcludedIngredients: productItem.excludedIngredients || [],
        existingSelectedAdditions: productItem.selectedAdditions || {},
        existingSelectedSupplements: productItem.selectedSupplements || {},
        existingQuantity: productItem.quantity || 1,
        discountReduction: (productItem as any).reduction || null,
        discountActivatedFor: (productItem as any).discountActivatedFor || [],
        discountOrderTypes: (productItem as any).discountOrderTypes || [],
        discountPromotionId: (productItem as any).discountPromotionId || null,
      }
    });
  }

  editBundleCartItem(item: CartItem): void {
    if (item.type !== CartItemType.BUNDLE) return;
    const bundleItem = item as BundleCartItem;
    const queryParams: any = {};
    if (bundleItem.redeemWithPoints) queryParams['mode'] = 'redeem';
    if (this.isCartSlideOpen) {
      this.toggleCart();
    }
    this.router.navigate(['/menus', bundleItem.slug], {
      queryParams: queryParams,
      state: {
        bundleId: bundleItem.bundle.id,
        editingCartItemId: bundleItem.id,
        existingSelectedItems: bundleItem.selectedItems,
        existingCustomizedItems: bundleItem.customizedItems,
      }
    });
  }

  editPromoCartItem(item: CartItem): void {
    if (item.type !== CartItemType.PROMOTION) return;
    const promoItem = item as PromotionCartItem;

    const queryParams: any = {};
    if (promoItem.redeemWithPoints) queryParams['mode'] = 'redeem';

    if (this.isCartSlideOpen) {
      this.toggleCart();
    }
    this.router.navigate(['/nos-promos', promoItem.promotion.slug], {
      queryParams: queryParams,
      state: {
        promoId: promoItem.promotion.id,
        editingCartItemId: promoItem.id,
        existingBuyItems: promoItem.buyItems,
        existingOfferedItems: promoItem.offeredItems,
        existingCustomizedItems: promoItem.customizedItems,
        selectedRuleRef: promoItem.promotion.payload?.selectedRuleRef ?? null,

      }
    });
  }

  trackCompositionLine(line: string, index: number) {
    return `${line}=${index}`
  }

  ngOnDestroy(): void {
    if (this.isBrowser) {
      document.body.style.overflow = '';
    }
  }
}
